/**
 * Analytics utilities for Merrily
 * Handles session tracking, event logging, and local storage management
 */

import { v4 as uuidv4 } from 'uuid';

// Session ID management
const SESSION_ID_KEY = 'rte_session_id';
const FIRST_SEARCH_KEY = 'rte_first_search_done';
const USER_LOCATION_KEY = 'rte_user_location';

/**
 * Get or create session ID
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = uuidv4();
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

/**
 * Check if user has done their first search (for homepage UI state)
 */
export const hasUserSearched = () => {
  return localStorage.getItem(FIRST_SEARCH_KEY) === 'true';
};

/**
 * Mark user as having completed their first search
 */
export const markFirstSearchDone = () => {
  localStorage.setItem(FIRST_SEARCH_KEY, 'true');
};

/**
 * Get user location from localStorage
 */
export const getUserLocation = () => {
  const location = localStorage.getItem(USER_LOCATION_KEY);
  return location ? JSON.parse(location) : null;
};

/**
 * Set user location in localStorage
 */
export const setUserLocation = (location) => {
  localStorage.setItem(USER_LOCATION_KEY, JSON.stringify(location));
};

/**
 * Log search event to analytics API
 */
export const logSearchEvent = async (searchParams) => {
  const sessionId = getSessionId();
  
  const payload = {
    sessionId,
    metro: searchParams.metro,
    lat: searchParams.lat, 
    lng: searchParams.lng,
    category: searchParams.category,
    subTheme: searchParams.subTheme,
    dateStart: searchParams.dateStart,
    dateEnd: searchParams.dateEnd,
    radiusMiles: searchParams.radiusMiles,
    resultCount: searchParams.resultCount
  };

  try {
    const response = await fetch(`${getApiBase()}/api/search-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      console.warn('Search event logging failed:', response.status);
    }
  } catch (error) {
    console.warn('Search event logging error:', error);
  }
};

/**
 * Log funnel event to analytics API
 */
const getApiBase = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;
  return useDevApiServer ? `http://localhost:${port}` : '';
};

export const logFunnelEvent = async (eventType, listingId = null, metadata = null) => {
  const sessionId = getSessionId();
  
  const payload = {
    sessionId,
    eventType,
    listingId,
    metadata
  };

  try {
    const response = await fetch(`${getApiBase()}/api/funnel-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      console.warn('Funnel event logging failed:', response.status);
    }
  } catch (error) {
    console.warn('Funnel event logging error:', error);
  }
};

/**
 * Get listing stats from analytics API
 */
export const getListingStats = async (listingId) => {
  try {
    const response = await fetch(`/api/listing-stats/${listingId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.warn('Listing stats fetch error:', error);
    return {
      listingId,
      viewCount: 0,
      uniqueViewers: 0,
      firstView: null,
      lastView: null
    };
  }
};

/**
 * Location utilities
 */

/**
 * Get user's geolocation via browser API
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      position => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      error => {
        reject(error);
      },
      {
        timeout: 10000,
        enableHighAccuracy: false,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

/**
 * Reverse geocode coordinates to location name via Mapbox
 */
export const reverseGeocode = async (lat, lng) => {
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  
  if (!accessToken) {
    throw new Error('Mapbox access token not configured');
  }
  
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${accessToken}&types=place`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      const place = data.features[0];
      return {
        city: place.text,
        region: place.context?.find(c => c.id.startsWith('region'))?.text || '',
        fullName: place.place_name
      };
    }
    
    throw new Error('No location found');
  } catch (error) {
    console.warn('Reverse geocoding failed:', error);
    return {
      city: 'Unknown Location',
      region: '',
      fullName: 'Unknown Location'
    };
  }
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in miles
};

/**
 * Convert radius to bounding box for Sharetribe bounds parameter
 */
/**
 * Auto-detect user location on first visit. Fires geolocation prompt once.
 * Saves to localStorage if granted. Silent fail if denied.
 */
export const autoDetectLocation = async () => {
  const saved = getUserLocation();
  if (saved && saved.lat) return saved; // Already have location

  try {
    const coords = await getCurrentLocation();
    const info = await reverseGeocode(coords.lat, coords.lng);
    const location = {
      ...coords,
      city: info.city,
      region: info.region,
      fullName: info.fullName,
      radius: 25
    };
    setUserLocation(location);
    return location;
  } catch (e) {
    // Silent fail — user denied or geolocation unavailable
    return null;
  }
};

export const radiusToBounds = (lat, lng, radiusMiles) => {
  // Rough conversion: 1 degree ≈ 69 miles
  const latDelta = radiusMiles / 69;
  const lngDelta = radiusMiles / (69 * Math.cos(lat * Math.PI / 180));
  
  return {
    ne: `${lat + latDelta},${lng + lngDelta}`,
    sw: `${lat - latDelta},${lng - lngDelta}`
  };
};